import React from 'react';
const { useState, useEffect, useRef } = React;
import { fmtDate, adminCall } from '../diagnostic/shared.jsx';
import { coachCall } from './shared.jsx';
import { navigate } from './ContactLink.jsx';
import { timeZoneOption } from '../../timeZones.js';
import { COACHING_MODALITIES, COACHING_FOCUS, PROCESS_STATUSES, label } from '../../coachingConfig.js';
import { ROLES, DIAGNOSTIC_STATUSES, t } from '../../diagnosticConfig.js';
import { LoadingState, ErrorState } from '../ui/States.jsx';
import PersonSpacePanel from './PersonSpacePanel.jsx';
import PersonJourneyPanel from './PersonJourneyPanel.jsx';
import PersonProgress from './PersonProgress.jsx';
import { AddMenu, CommercialBlock, LearningBlock, PendingBlock, FollowUpForm, HistoryBlock } from './PersonServices.jsx';
import { isTestName } from '../../testRecords.js';

// A person's card (Personas; coach only; coaching-admin › personCard decides what
// comes): the identity, the next step, every service (commercial, Diagnostic,
// coaching, courses), pending, the follow-ups and a filterable history, the
// access and the exact preview of their space. Of each service only what the
// coach may see: never coaching notes of sessions, reflections, answers or the
// text of messages. The follow-ups written here are internal (only the coach) or
// a progress sent to the person as a message of their process.

const card = { background: 'var(--globant-card)', border: '1px solid var(--globant-border)', borderRadius: 16, padding: 20 };
const muted = { fontSize: 13, color: 'var(--globant-muted)' };
const h3 = { margin: 0, fontSize: 17 };
const AUD = {
  business: ['var(--area-business-bg)', 'var(--area-business)', 'Comercial'],
  diagnostic: ['var(--area-diagnostic-bg)', 'var(--area-diagnostic)', 'Administración de Oike'],
  coaching: ['var(--area-coaching-bg)', 'var(--area-coaching)', 'Solo coach'],
  private: ['var(--area-private-bg)', 'var(--area-private)', 'Solo la persona'],
};
const Badge = ({ who, text }) => <span style={{ fontSize: 12, fontWeight: 600, padding: '2px 10px', borderRadius: 999, background: AUD[who][0], color: AUD[who][1], whiteSpace: 'nowrap' }}>{text || AUD[who][2]}</span>;
const chip = (bg, color) => ({ fontSize: 13, padding: '3px 10px', borderRadius: 999, background: bg, color });
const DIAG_STATUS = Object.fromEntries(DIAGNOSTIC_STATUSES.map(s => [s.key, s.label]));
const STATUS_ES = { sent: 'enviado', received: 'recibido', replied: 'respondido', opened: 'abierto', bounced: 'rebotado', scheduled: 'programado', draft: 'borrador', meeting: 'reunión' };
const statusEs = (v) => STATUS_ES[String(v || '').toLowerCase()] || v;
const initials = (name) => String(name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('');

function Dots({ done, total }) {
  if (!total) return <span style={muted}>Sin sesiones todavía</span>;
  return (
    <div style={{ display: 'flex', gap: 4 }} role="progressbar" aria-label={`${done} de ${total} sesiones`} aria-valuemin={0} aria-valuemax={total} aria-valuenow={done}>
      {Array.from({ length: total }, (_, i) => <span key={i} style={{ flex: 1, height: 8, borderRadius: 4, background: i < done ? 'var(--globant-green)' : i === done ? '#9FD3CB' : '#EEEAE2' }} />)}
    </div>
  );
}

/** The coach's card of a person (coaching-admin › personCard, coach only) and her diagnostics (administration). */
export function usePersonCard(personId) {
  const [v, setV] = useState(null);
  const [error, setError] = useState('');
  const [diagnostics, setDiagnostics] = useState(null);
  const [diagError, setDiagError] = useState('');
  const load = () => {
    if (!personId) return;
    setError('');
    coachCall({ action: 'personCard', personId }).then(setV).catch(e => setError(e.message));
    adminCall({ action: 'personDiagnostics', personId }).then(d => { setDiagnostics(d.diagnostics); setDiagError(''); }).catch(e => { setDiagnostics([]); setDiagError(e.message || 'No se pudieron leer sus diagnósticos.'); });
  };
  useEffect(load, [personId]); // eslint-disable-line react-hooks/exhaustive-deps
  return { v, error, diagnostics, diagError, load };
}

/** Header of the card: identity and the person's basic data (edit only her card). */
export function PersonHeader({ v, onEdit }) {
  const p = v.person;
  const com = v.commercial;
  const companies = com?.accounts?.map(a => a.name).filter(Boolean) || [];
  const tz = p.timeZone ? timeZoneOption(p.timeZone)?.label || p.timeZone : null;
  return (
      <section style={{ ...card, display: 'flex', flexWrap: 'wrap', gap: '14px 22px', alignItems: 'center' }}>
        <div aria-hidden="true" style={{ width: 56, height: 56, borderRadius: 28, background: 'var(--teal-dim)', color: '#155C54', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 20, flex: 'none' }}>{initials(p.name)}</div>
        <div style={{ flex: '1 1 260px', minWidth: 0 }}>
          <h2 style={{ margin: 0, fontSize: 24, letterSpacing: '-0.3px' }}>{p.name}</h2>
          {isTestName(p.name) && <div role="note" style={{ display: 'inline-block', marginTop: 4, fontSize: 12, fontWeight: 700, padding: '2px 10px', borderRadius: 999, background: '#FBF0DC', color: '#6B4510' }}>Registro de prueba · fuera de informes y métricas</div>}
          <div style={{ ...muted, fontSize: 14 }}>{[p.email, tz].filter(Boolean).join(' · ') || 'Sin email'}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
            <span style={chip('#F1EFEA', 'var(--globant-text-secondary)')}>{companies.length ? `Empresa del contacto: ${companies.join(', ')}` : 'Sin empresa'}</span>
            {com && <span style={chip('var(--area-business-bg)', 'var(--area-business)')} title={com.firstOrigin?.known ? com.firstOrigin.methodLabel : undefined}>Primer origen: {com.firstOrigin?.label || 'Sin identificar'}</span>}
            {com?.source && <span style={chip('var(--area-business-bg)', 'var(--area-business)')}>Categoría comercial: {com.source}</span>}
            <span style={chip(p.stakeholderId ? 'var(--area-business-bg)' : '#F1EFEA', p.stakeholderId ? 'var(--area-business)' : 'var(--globant-text-secondary)')}>{p.stakeholderId ? 'Contacto comercial vinculado' : 'Sin contacto comercial'}</span>
            {v.createdAt && <span style={chip('#F1EFEA', 'var(--globant-text-secondary)')}>Ficha desde {fmtDate(v.createdAt)}</span>}
          </div>
        </div>
        <div style={{ display: 'grid', gap: 6, flex: '0 1 220px' }}>
          <button type="button" className="action-btn" onClick={() => onEdit(p)}>Editar ficha</button>
          <span style={{ ...muted, fontSize: 12 }}>Datos básicos. La empresa va en cada recorrido.</span>
        </div>
      </section>
  );
}

/** Resumen: the next step, progress and what needs attention. */
export function PersonSummary({ v, onOpenProcess, onChanged, progressPart = null }) {
  return (
    <div style={{ display: 'grid', gap: 16 }}>
          {v.next && (
            <section style={{ ...card, background: 'var(--globant-green)', borderColor: 'var(--globant-green)', color: '#FFFFFF', display: 'flex', flexWrap: 'wrap', gap: '10px 20px', alignItems: 'center' }}>
              <div style={{ flex: '1 1 240px' }}>
                <div style={{ fontSize: 13, opacity: 0.9 }}>Próximo paso</div>
                <div style={{ fontSize: 20, fontWeight: 700 }}>{v.next.title}</div>
                <div style={{ fontSize: 14, opacity: 0.92 }}>{[v.next.when && fmtDate(v.next.when, true), v.next.processName].filter(Boolean).join(' · ')}</div>
              </div>
              <button type="button" className="action-btn" style={{ background: '#FFFFFF', color: '#155C54', border: 0, minHeight: 44, padding: '0 18px' }} onClick={() => onOpenProcess(v.next.processId)}>Abrir proceso</button>
            </section>
          )}
      <PersonProgress v={v} onChanged={onChanged} part={progressPart} />
      <PendingBlock v={v} />
    </div>
  );
}

/** Servicios: what to add, every participation (processes, diagnostics, teams she leads), courses and Mi recorrido. */
export function PersonServicesSection({ v, diagnostics, diagError = '', onOpenProcess, onChanged, onNotes = null }) {
  const p = v.person;
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <AddMenu v={v} onDone={onChanged} onFollowUp={onNotes || (() => {})} />
      {diagError && <div role="alert" style={{ ...muted, color: '#9A6212' }}>No se pudieron leer sus diagnósticos ({diagError}). No significa que no participe en ninguno.</div>}
          <section style={card}>
            <h3 style={h3}>Recorridos</h3>
            <p style={{ ...muted, margin: '2px 0 12px' }}>Cada participación tiene su empresa, su contenido y sus permisos.</p>
            <div style={{ display: 'grid', gap: 12 }}>
              {v.participations.map(x => (
                <div key={x.memberId} style={{ border: '1px solid var(--globant-border)', borderRadius: 14, padding: 16, display: 'grid', gap: 8, opacity: x.memberStatus === 'left' ? 0.75 : 1 }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', justifyContent: 'space-between' }}>
                    <b>{x.process.name}</b>
                    <Badge who="coaching" />
                  </div>
                  <div style={{ ...muted, fontSize: 14 }}>
                    {[label(COACHING_MODALITIES, x.process.modality), x.process.focus && `foco ${label(COACHING_FOCUS, x.process.focus).toLowerCase()}`, x.memberStatus === 'left' ? 'ya no participa' : label(PROCESS_STATUSES, x.process.status)].filter(Boolean).join(' · ')}
                  </div>
                  <Dots done={x.sessions.done} total={x.sessions.total} />
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <button type="button" className="action-btn btn-primary" onClick={() => onOpenProcess(x.process.id)}>Abrir proceso</button>
                    {x.next && <span style={muted}>Siguiente: {x.next.title}{x.next.when ? ` · ${fmtDate(x.next.when, true)}` : ''}</span>}
                  </div>
                </div>
              ))}
              {(diagnostics || []).map(d => (
                <div key={d.participantId} style={{ border: '1px solid var(--globant-border)', borderRadius: 14, padding: 16, display: 'grid', gap: 6 }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', justifyContent: 'space-between' }}>
                    <b>Diagnostic · {d.diagnosticName}</b>
                    <Badge who="diagnostic" />
                  </div>
                  <div style={{ ...muted, fontSize: 14 }}>
                    {[t(ROLES[d.role]?.label) || d.role, t(DIAG_STATUS[d.status]) || d.status, d.accountName && `empresa de esa participación: ${d.accountName}`, d.isTest && 'prueba'].filter(Boolean).join(' · ')}
                  </div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    {d.accountId && <button type="button" className="action-btn" onClick={() => { window.__oikePendingDiagnostic = d.diagnosticId; navigate({ page: 'accounts', accountId: d.accountId, tab: 'diagnostic', diagnosticId: d.diagnosticId }); }}>Abrir diagnóstico</button>}
                    <span style={{ ...muted, fontSize: 12 }}>Las respuestas, sesiones y evaluaciones quedan en el diagnóstico (ahí se abren los formularios).</span>
                  </div>
                </div>
              ))}
              {(v.sponsored || []).map(c => (
                <div key={c.key} style={{ border: '1px solid var(--globant-border)', borderRadius: 14, padding: 16, display: 'grid', gap: 6 }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', justifyContent: 'space-between' }}>
                    <b>{c.name}</b>
                    <Badge who="coaching" text="Quien contrata" />
                  </div>
                  <div style={{ ...muted, fontSize: 14 }}>Ve la vista grupal del equipo desde su espacio · {c.statusLabel}</div>
                  <div><button type="button" className="action-btn" onClick={() => onOpenProcess(c.id)}>Abrir proceso</button></div>
                </div>
              ))}
              {!v.participations.length && diagnostics && !diagnostics.length && !diagError && !v.learning?.length && !(v.sponsored || []).length && <div style={muted}>Todavía no participa en procesos, diagnósticos ni cursos.</div>}
            </div>
          </section>
      <LearningBlock v={v} />
      <PersonJourneyPanel personId={p.id} name={p.name} onChanged={onChanged} />
    </div>
  );
}

/** Mensajes: only metadata of the conversations (commercial interactions and private messages of each process). */
export function PersonConversations({ v, onOpenProcess, initial = 'all' }) {
  const [conv, setConv] = useState(initial);
  const p = v.person;
  const com = v.commercial;
  const active = v.participations.filter(x => x.memberStatus !== 'left' && x.process.status !== 'closed');
  const showCom = conv !== 'coaching';
  const showCoach = conv !== 'business';
  return (
          <section style={card}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={h3}>Conversaciones</h3>
              <div role="group" aria-label="Filtrar conversaciones" style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {[['all', 'Todas'], ['business', 'Comerciales'], ['coaching', 'Acompañamiento']].map(([k, l]) => (
                  <button key={k} type="button" aria-pressed={conv === k} onClick={() => setConv(k)}
                    style={{ border: `1px solid ${conv === k ? 'var(--globant-green)' : 'var(--globant-border)'}`, background: conv === k ? 'var(--teal-dim)' : '#FFFFFF', color: conv === k ? '#155C54' : 'var(--globant-text-secondary)', borderRadius: 999, padding: '0 12px', minHeight: 36, font: 'inherit', fontSize: 14, cursor: 'pointer' }}>{l}</button>
                ))}
              </div>
            </div>
            {showCom && (
              <div style={{ marginBottom: showCoach ? 14 : 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--area-business)', marginBottom: 6 }}>Comerciales · las ve el equipo comercial</div>
                {!p.stakeholderId ? <div style={muted}>Sin contacto comercial vinculado.</div>
                  : !com?.interactions?.length ? <div style={muted}>Sin interacciones registradas.</div>
                  : <div style={{ display: 'grid', gap: 6 }}>
                    {com.interactions.map(i => (
                      <div key={i.id} style={{ background: '#FBF8F1', borderRadius: 12, padding: '8px 12px', fontSize: 14 }}>
                        <b>{i.channel || 'Interacción'}</b>{i.direction ? ` · ${/in/i.test(i.direction) ? 'recibida' : 'enviada'}` : ''}{i.status && !/^(sent|received)$/i.test(i.status) ? ` · ${statusEs(i.status)}` : ''}<span style={muted}>{i.date ? ` · ${fmtDate(i.date)}` : ''}</span>
                      </div>
                    ))}
                  </div>}
                {p.stakeholderId && <button type="button" className="action-btn" style={{ marginTop: 8, fontSize: 13 }} onClick={() => navigate({ page: 'contacts', contactId: p.stakeholderId })}>Ver el historial en Contactos</button>}
              </div>
            )}
            {showCoach && (
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--area-coaching)', marginBottom: 6 }}>Acompañamiento · solo tú y {p.name.split(' ')[0]}</div>
                {!active.length ? <div style={muted}>Sin procesos activos.</div> : (
                  <div style={{ display: 'grid', gap: 6 }}>
                    {active.map(x => (
                      <div key={x.memberId} style={{ background: '#F1F8F6', borderRadius: 12, padding: '8px 12px', fontSize: 14, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                        <span style={{ flex: '1 1 200px' }}><b>{x.process.name}</b> · {x.messages.count ? `${x.messages.count} mensaje${x.messages.count === 1 ? '' : 's'} privados` : 'sin mensajes'}{x.messages.unread ? ` · ${x.messages.unread} sin leer` : ''}<span style={muted}>{x.messages.lastAt ? ` · último ${fmtDate(x.messages.lastAt, true)}` : ''}</span></span>
                        <button type="button" className="action-btn" style={{ fontSize: 13 }} onClick={() => onOpenProcess(x.process.id, { tab: 'messages', thread: x.memberId })}>Abrir mensajes</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </section>
  );
}

/** Notas y avances: internal notes (only the coach) or progress shared with the person, by service, and their history. */
export function PersonNotes({ v, onChanged, formRef = null }) {
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <FollowUpForm v={v} onDone={onChanged} formRef={formRef} />
      <HistoryBlock v={v} onDeleted={onChanged} />
    </div>
  );
}

/** Acceso: her personal access, «Ver como», who sees what and the history of the link with her contact. */
export function PersonAccess({ v }) {
  const p = v.person;
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <PersonSpacePanel personId={p.id} name={p.name} email={p.email || ''} />
          <section style={card}>
            <h3 style={{ ...h3, fontSize: 15, marginBottom: 10 }}>Quién ve qué</h3>
            <div style={{ display: 'grid', gap: 10, fontSize: 14 }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}><Badge who="business" /><span>Origen, contacto y conversaciones comerciales.</span></div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}><Badge who="diagnostic" text="Diagnostic" /><span>Participación, rol y estado. Las respuestas quedan en el diagnóstico.</span></div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}><Badge who="coaching" /><span>Procesos, sesiones, notas y mensajes.</span></div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}><Badge who="private" /><span>Sus reflexiones privadas, hasta que decida compartirlas.</span></div>
            </div>
          </section>
          <section style={{ ...card, borderStyle: 'dashed', ...muted, fontSize: 14 }}>
            Las reflexiones privadas de {p.name.split(' ')[0]} no aparecen aquí. Solo las ve quien las escribe.
          </section>
          {p.linkHistory?.length > 0 && (
            <details style={card}>
              <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Historial del vínculo con el contacto</summary>
              <div style={{ display: 'grid', gap: 4, marginTop: 8, ...muted }}>
                {p.linkHistory.map((h, i) => <div key={i}>{fmtDate(h.at, true)} · {h.to ? (h.from ? 'cambió de contacto' : 'vinculada') : 'desvinculada'}{h.note ? ` · ${h.note}` : ''}</div>)}
              </div>
            </details>
          )}
    </div>
  );
}

/** The card as one page (Fichas de participantes keeps it; the unified client file shows the same parts in tabs). */
export default function PersonProfile({ personId, onBack, onEdit, onOpenProcess }) {
  const { v, error, diagnostics, diagError, load } = usePersonCard(personId);
  const followUpRef = useRef(null);
  const back = <button type="button" className="action-btn" onClick={onBack}>← Personas</button>;
  if (error) return <div style={{ display: 'grid', gap: 12 }}>{back}<ErrorState message={error} onRetry={load} /></div>;
  if (!v) return <div>{back}<LoadingState /></div>;
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div>{back}</div>
      <PersonHeader v={v} onEdit={onEdit} />
      <PersonSummary v={v} onOpenProcess={onOpenProcess} onChanged={load} />
      <PersonServicesSection v={v} diagnostics={diagnostics} diagError={diagError} onOpenProcess={onOpenProcess} onChanged={load} onNotes={() => followUpRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })} />
      <CommercialBlock v={v} onChanged={load} />
      <PersonNotes v={v} onChanged={load} formRef={followUpRef} />
      <PersonAccess v={v} />
      <PersonConversations v={v} onOpenProcess={onOpenProcess} />
    </div>
  );
}
